import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@shared/ui/dialog";
import { Button } from "@shared/ui/button";
import { Label } from "@shared/ui/label";

export interface RejectRequestDialogueProps {
  isOpen: boolean;
  onClose: () => void;
  onReject: (reason: string) => Promise<void> | void;
  onCancel?: () => void;
  title: string;
  description?: string;
  label: string;
  placeholder: string;
  rejectButtonText: string;
  cancelButtonText: string;
  isSubmitting?: boolean;
  validateReason?: (reason: string) => string | null;
  icon?: React.ReactNode;
}

export function RejectRequestDialogue({
  isOpen,
  onClose,
  onReject,
  onCancel,
  title,
  description,
  label,
  placeholder,
  rejectButtonText,
  cancelButtonText,
  isSubmitting = false,
  validateReason,
  icon,
}: RejectRequestDialogueProps) {
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setReason("");
      setError(null);
      setSubmitting(false);
    }
  }, [isOpen]);

  const handleReasonChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setReason(e.target.value);
    setError(null);
  };

  const getValidationError = (val: string) => {
    if (validateReason) return validateReason(val);
    return val.trim() ? null : "Reason is required.";
  };

  const handleReject = async () => {
    const validationError = getValidationError(reason);
    if (validationError) {
      setError(validationError);
      return;
    }
    setSubmitting(true);
    try {
      await onReject(reason);
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (onCancel) onCancel();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {icon}
            {title}
          </DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="rejection-reason">{label}</Label>
            <textarea
              id="rejection-reason"
              className="mt-2 w-full min-h-[100px] border rounded-md p-3 text-base focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none"
              placeholder={placeholder}
              value={reason}
              onChange={handleReasonChange}
              disabled={isSubmitting || submitting}
              autoFocus
            />
            {error && <div className="text-red-600 text-sm mt-1">{error}</div>}
          </div>
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={isSubmitting || submitting}
            >
              {cancelButtonText}
            </Button>
            <Button
              onClick={handleReject}
              disabled={isSubmitting || submitting}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {rejectButtonText}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
} 